import { Link } from "react-router-dom";
import Header from "../components/Header";
import { ArrowLeft, Shield, RotateCcw, Package, AlertCircle, CheckCircle, Clock, Mail } from "lucide-react";

export default function WarrantiesReturns({ user, logout }) {
  return (
    <div className="min-h-screen bg-background">
      <Header user={user} logout={logout} />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Link */}
          <Link 
            to="/" 
            className="inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-8"
            data-testid="back-to-home"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver al inicio
          </Link>

          {/* Header */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                <RotateCcw className="h-6 w-6 text-primary" />
              </div>
              <h1 className="text-3xl md:text-4xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }} data-testid="warranties-title">
                Garantías y Devoluciones
              </h1>
            </div>
            <p className="text-muted-foreground">
              Última actualización: Enero 2025
            </p>
          </div>

          {/* Content */}
          <div className="prose prose-gray max-w-none space-y-8">
            
            {/* Intro */}
            <section className="bg-gray-50 rounded-2xl p-6 border" data-testid="warranties-intro">
              <p className="text-lg leading-relaxed">
                En <strong>BulkBytes</strong> queremos que estés completamente satisfecho con tu compra. 
                Aquí encontrarás toda la información sobre garantías, devoluciones y el proceso de reclamación.
              </p>
            </section>

            {/* Important Notice */}
            <section className="bg-orange-50 rounded-2xl p-6 border border-orange-200" data-testid="warranties-notice">
              <div className="flex gap-4">
                <div className="flex-shrink-0">
                  <AlertCircle className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-orange-800 mb-2">Importante sobre el modelo de compra grupal</h3>
                  <p className="text-orange-700 text-sm">
                    Recuerda que en BulkBytes las compras funcionan por grupos. Una vez que te unes a una oferta 
                    y realizas el pago, <strong>no es posible cancelar voluntariamente</strong> hasta que expire 
                    el plazo. Si la oferta no alcanza el mínimo de participantes, recibirás un reembolso automático.
                  </p>
                </div>
              </div>
            </section>

            {/* Section 1 - Garantías */}
            <section data-testid="warranties-section-1">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <Shield className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Garantías
                </h2>
              </div>

              {/* Garantía según condición */}
              <div className="grid gap-6 md:grid-cols-3 mb-8">
                <div className="bg-white p-6 rounded-xl border">
                  <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <span className="text-blue-600 font-bold">N</span>
                  </div>
                  <h3 className="font-semibold mb-2">Productos Nuevos</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Garantía oficial del fabricante (Apple)
                  </p>
                  <p className="text-2xl font-bold text-green-600">2 años</p>
                </div>

                <div className="bg-white p-6 rounded-xl border">
                  <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center mb-4">
                    <span className="text-purple-600 font-bold">CPO</span>
                  </div>
                  <h3 className="font-semibold mb-2">Productos CPO</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Certificado y reacondicionado por Apple
                  </p>
                  <p className="text-2xl font-bold text-green-600">1 año</p>
                </div>

                <div className="bg-white p-6 rounded-xl border">
                  <div className="w-10 h-10 rounded-full bg-orange-100 flex items-center justify-center mb-4">
                    <span className="text-orange-600 font-bold">U</span>
                  </div>
                  <h3 className="font-semibold mb-2">Productos Usados</h3>
                  <p className="text-sm text-muted-foreground mb-3">
                    Garantía BulkBytes por defectos ocultos
                  </p>
                  <p className="text-2xl font-bold text-green-600">6 meses</p>
                </div>
              </div>

              {/* Qué cubre */}
              <div className="space-y-6">
                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    ¿Qué cubre la garantía?
                  </h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Defectos de fabricación</li>
                    <li>Fallos de hardware no provocados por el usuario</li>
                    <li>Problemas de batería por debajo del 80% de capacidad (en productos usados verificados)</li>
                    <li>Defectos ocultos no detectables en el momento de la compra</li>
                  </ul>
                </div>

                <div>
                  <h3 className="font-semibold mb-3 flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    ¿Qué NO cubre la garantía?
                  </h3>
                  <ul className="list-disc list-inside text-muted-foreground space-y-2">
                    <li>Daños por golpes, caídas o líquidos</li>
                    <li>Desgaste normal del producto</li>
                    <li>Daños por mal uso o manipulación incorrecta</li>
                    <li>Reparaciones realizadas por terceros no autorizados</li>
                    <li>Productos modificados o con software no oficial</li>
                    <li>Accesorios (cables, cargadores, auriculares) después de 30 días</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 2 - Devoluciones */}
            <section data-testid="warranties-section-2">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                  <RotateCcw className="h-5 w-5 text-blue-600" />
                </div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Devoluciones
                </h2>
              </div>

              <div className="bg-white rounded-xl border p-6 mb-6">
                <h3 className="font-semibold mb-4">Derecho de desistimiento (14 días)</h3>
                <p className="text-muted-foreground mb-4">
                  De acuerdo con la legislación española y europea, dispones de <strong>14 días naturales</strong> desde 
                  la recepción del producto para ejercer tu derecho de desistimiento sin necesidad de justificación.
                </p>
                <div className="bg-gray-50 rounded-lg p-4">
                  <h4 className="font-medium mb-2">Condiciones para la devolución:</h4>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li>✓ El producto debe estar en su estado original, sin usar</li>
                    <li>✓ Debe incluir todos los accesorios y embalaje original</li>
                    <li>✓ No debe haber sido activado (para productos Apple, no debe haberse vinculado a un Apple ID)</li>
                    <li>✓ Debes comunicar tu intención de devolución antes del plazo</li>
                  </ul>
                </div>
              </div>

              {/* Proceso de devolución */}
              <div className="space-y-4">
                <h3 className="font-semibold">Proceso de devolución</h3>
                
                <div className="grid gap-4">
                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">1</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Contacta con nosotros</h4>
                      <p className="text-sm text-muted-foreground">
                        Envía un email a bulkbytes26@gmail.com indicando tu número de pedido y el motivo.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">2</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Recibe la etiqueta de envío</h4>
                      <p className="text-sm text-muted-foreground">
                        Te enviaremos una etiqueta de envío prepagada o instrucciones para el envío.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">3</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Prepara el paquete</h4>
                      <p className="text-sm text-muted-foreground">
                        Empaqueta el producto de forma segura con todos sus accesorios y embalaje original.
                      </p>
                    </div>
                  </div>

                  <div className="flex gap-4 items-start">
                    <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">4</span>
                    </div>
                    <div>
                      <h4 className="font-medium">Recibe el reembolso</h4>
                      <p className="text-sm text-muted-foreground">
                        Una vez verificado el estado del producto, procesaremos el reembolso en 5-10 días laborables.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Costes */}
              <div className="mt-6 bg-yellow-50 rounded-xl border border-yellow-200 p-6">
                <h4 className="font-semibold mb-2">Costes de devolución</h4>
                <ul className="text-sm text-muted-foreground space-y-2">
                  <li><strong>Desistimiento (cambio de opinión):</strong> El coste del envío de devolución corre a cargo del cliente (aprox. 8-15€)</li>
                  <li><strong>Producto defectuoso o error nuestro:</strong> Nosotros asumimos todos los gastos de envío</li>
                  <li><strong>Producto diferente al descrito:</strong> Nosotros asumimos todos los gastos de envío</li>
                </ul>
              </div>
            </section>

            {/* Section 3 - Reclamaciones */}
            <section data-testid="warranties-section-3">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Reclamaciones y Productos Defectuosos
                </h2>
              </div>

              <div className="space-y-6">
                <p className="text-muted-foreground">
                  Si recibes un producto defectuoso o que no coincide con la descripción, tienes derecho a:
                </p>

                <div className="grid gap-4 md:grid-cols-2">
                  <div className="bg-white p-5 rounded-xl border">
                    <h4 className="font-semibold mb-2">Reparación gratuita</h4>
                    <p className="text-sm text-muted-foreground">
                      Enviaremos el producto a reparar sin coste adicional para ti.
                    </p>
                  </div>
                  <div className="bg-white p-5 rounded-xl border">
                    <h4 className="font-semibold mb-2">Sustitución</h4>
                    <p className="text-sm text-muted-foreground">
                      Te enviaremos un producto nuevo o equivalente si la reparación no es posible.
                    </p>
                  </div>
                  <div className="bg-white p-5 rounded-xl border">
                    <h4 className="font-semibold mb-2">Reembolso parcial</h4>
                    <p className="text-sm text-muted-foreground">
                      Rebaja proporcional del precio si el defecto es menor y aceptas quedarte el producto.
                    </p>
                  </div>
                  <div className="bg-white p-5 rounded-xl border">
                    <h4 className="font-semibold mb-2">Reembolso total</h4>
                    <p className="text-sm text-muted-foreground">
                      Devolución completa del importe si el defecto es grave o la reparación/sustitución no es viable.
                    </p>
                  </div>
                </div>

                <div className="bg-gray-50 rounded-xl p-6 border">
                  <div className="flex items-center gap-3 mb-4">
                    <Clock className="h-5 w-5 text-primary" />
                    <h4 className="font-semibold">Plazos de respuesta</h4>
                  </div>
                  <ul className="text-sm text-muted-foreground space-y-2">
                    <li><strong>Respuesta inicial:</strong> 24-48 horas laborables</li>
                    <li><strong>Resolución de incidencias simples:</strong> 5-7 días laborables</li>
                    <li><strong>Reparaciones:</strong> 15-30 días laborables según el caso</li>
                    <li><strong>Reembolsos:</strong> 5-10 días laborables desde la aprobación</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* Section 4 - Reembolsos automáticos */}
            <section data-testid="warranties-section-4">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                  <RotateCcw className="h-5 w-5 text-green-600" />
                </div>
                <h2 className="text-2xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Reembolsos Automáticos (Ofertas no completadas)
                </h2>
              </div>

              <div className="bg-green-50 rounded-xl border border-green-200 p-6">
                <p className="text-muted-foreground mb-4">
                  Si una oferta grupal no alcanza el número mínimo de participantes en el plazo establecido:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">
                      <strong>Reembolso automático:</strong> Se procesa sin que tengas que hacer nada
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">
                      <strong>Mismo método de pago:</strong> El dinero vuelve a tu tarjeta o cuenta original
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">
                      <strong>Plazo:</strong> Entre 5-10 días laborables desde la expiración de la oferta
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-muted-foreground">
                      <strong>Notificación:</strong> Recibirás un email confirmando el reembolso
                    </span>
                  </li>
                </ul>
              </div>
            </section>

            {/* Contact */}
            <section className="bg-primary/5 rounded-2xl p-6 border border-primary/20" data-testid="warranties-contact">
              <div className="flex items-center gap-3 mb-4">
                <Mail className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-semibold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  ¿Necesitas ayuda?
                </h2>
              </div>
              <p className="text-muted-foreground mb-4">
                Nuestro equipo de atención al cliente está disponible para resolver cualquier duda sobre 
                garantías, devoluciones o reclamaciones.
              </p>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <p className="font-medium">Devoluciones y garantías:</p>
                  <p className="text-muted-foreground">bulkbytes26@gmail.com</p>
                </div>
                <div>
                  <p className="font-medium">Atención general:</p>
                  <p className="text-muted-foreground">bulkbytes26@gmail.com</p>
                </div>
              </div>
              <p className="text-sm text-muted-foreground mt-4">
                Horario: Lunes a Viernes de 9:00 a 18:00 (CET)
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 border-t">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm text-muted-foreground">
            © 2025 BulkBytes. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
